/**
 * Contradiction state for the ChartMind recording step.
 *
 * The clarifying-questions prompt returns, alongside its questions, the conflicts it can see
 * between what the patient is saying and (a) their imported chart record or (b) what they
 * said earlier in the same encounter. A patient who forgets their penicillin allergy and
 * reports "no allergies" is the case this exists for: nobody here can tell which side is
 * right, so the job is to put the disagreement in front of the clinician while the patient
 * is still in the room.
 *
 * Everything below is pure, because the interesting part is not the LLM call — it is keeping
 * a flag's identity stable across a poll that re-runs every 10 seconds. Without that, a
 * contradiction cannot be struck through when it is settled; it just silently disappears and
 * reappears as the model's attention wanders.
 *
 * Identity is COMPUTED HERE from what the model named (topic + subject), never asked for as an
 * id. Two jobs were easy to conflate: matching a flag across polls, and judging that the
 * patient has since clarified it. The second genuinely needs the model — it is a reading of
 * the conversation. The first is string handling, and asking a model to invent an identifier
 * and reproduce it verbatim every ten seconds is asking it to do bookkeeping instead.
 */

import { djb2 } from "utils/hash";

/**
 * Who last set a flag's open/resolved status.
 *
 * Tracked in BOTH directions, which is the whole point: a clinician who strikes a false
 * positive must not have it un-struck by the next poll, and a clinician who un-strikes
 * something they disagree was clarified must not have it re-struck either. Once a human has
 * ruled on a flag, the human owns its status — the model may still refresh the wording.
 */
export const STATUS_SET_BY = {
  MODEL: "model",
  CLINICIAN: "clinician",
};

const CONTRADICTION_KEYS = ["contradictions", "conflicts", "discrepancies"];

const CONFLICT_WITH_VALUES = new Set(["record", "encounter"]);

const asText = (value) => (value == null ? "" : String(value).trim());

/**
 * Slug fragment: lowercase, punctuation and spacing collapsed to single hyphens.
 *
 * This is the whole reason identity is computed here rather than asked for. "Penicillin",
 * "penicillin.", and "Penicillin " are the same drug and must produce the same fragment; a
 * model asked to reproduce a slug convention would eventually return three different ones.
 */
const slug = (value) =>
  asText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * The identity of a contradiction, derived from what the model NAMED rather than from an
 * identifier it was asked to invent and then remember.
 *
 * The model is asked for two facts it is good at: the topic ("Allergies") and the clinical
 * subject ("penicillin"). Those compose into the key we match on across polls. Asking instead
 * for a stable `id` would make cross-poll identity depend on the model reproducing a slug
 * convention verbatim every ten seconds, which is bookkeeping, not clinical reading.
 *
 * Falls back to hashing the detail when no subject was named, so an entry still gets a key.
 * That fallback IS wording-sensitive — a reworded detail hashes differently and appears as a
 * second flag — which is why the prompt asks for a subject on every entry. A duplicate is the
 * safe direction to fail: nothing is hidden, and the merge rules never drop anything.
 */
const deriveId = ({ topic, subject, detail }) => {
  const subjectSlug = slug(subject);
  if (subjectSlug) {
    const topicSlug = slug(topic);
    return topicSlug ? `${topicSlug}:${subjectSlug}` : subjectSlug;
  }
  return `detail-${djb2(slug(detail))}`;
};

/**
 * One contradiction from the model's JSON, or null if there is nothing to show a clinician.
 * `detail` is the whole point of the row, so an entry without it is dropped rather than
 * rendered as an empty red line.
 *
 * Any `id` the model volunteers is IGNORED. One code path for identity beats a split where
 * some flags are keyed the model's way and some ours, and the derived key is the one we can
 * reason about.
 */
const normalizeContradiction = (raw) => {
  if (!raw || typeof raw !== "object") return null;

  const detail = asText(raw.detail || raw.description || raw.summary || raw.text);
  if (!detail) return null;

  const topic = asText(raw.topic || raw.category);
  const subject = asText(raw.subject || raw.about || raw.item);
  const id = deriveId({ topic, subject, detail });
  const conflictWith = asText(raw.conflictWith || raw.conflict_with).toLowerCase();
  const resolved = asText(raw.status).toLowerCase() === "resolved";

  return {
    id,
    topic,
    subject,
    detail,
    // Anything the model invents outside the two known values reads as "we do not know which
    // side this came from", which the component renders without a source line.
    conflictWith: CONFLICT_WITH_VALUES.has(conflictWith) ? conflictWith : null,
    resolved,
    resolution: resolved ? asText(raw.resolution) : "",
    statusSetBy: STATUS_SET_BY.MODEL,
  };
};

/**
 * Contradictions out of a parsed LLM response, tolerating the shapes models actually return
 * (wrapped under any of several keys, or a bare array). Never throws and never returns
 * anything but an array: a response with no contradictions key is the normal case, both for
 * an encounter with nothing wrong in it and for a prompt that predates this feature.
 */
export function parseContradictions(parsed) {
  const list = Array.isArray(parsed)
    ? parsed
    : parsed && typeof parsed === "object"
      ? CONTRADICTION_KEYS.map((key) => parsed[key]).find(Array.isArray)
      : null;

  if (!Array.isArray(list)) return [];

  // Last one wins on a duplicate id, so a model that repeats itself within one response does
  // not produce two identical red lines.
  const byId = new Map();
  for (const raw of list) {
    const item = normalizeContradiction(raw);
    if (item) byId.set(item.id, item);
  }
  return [...byId.values()];
}

/**
 * The block appended to each poll's user message listing what we already flagged.
 *
 * Identity is derived here, not asked for, so this block is NOT how ids stay stable. It does
 * two other jobs:
 *
 *   - Resolution. Only the model can judge that the transcript has since settled a conflict,
 *     and it can only say so about conflicts it knows we are showing.
 *   - Consistent naming. Listing the subject already flagged ("penicillin") nudges the next
 *     response to name it the same way, which is what our derived key depends on.
 *
 * Subjects rather than slugs, because the subject is the thing the model reads and writes in
 * clinical terms. Returns "" when there is nothing known yet, so callers can concatenate
 * blindly.
 */
export function formatKnownContradictionsBlock(contradictions) {
  if (!Array.isArray(contradictions) || contradictions.length === 0) return "";

  const lines = contradictions.map((c) => {
    const label = [c.topic, c.subject].filter(Boolean).join(" / ") || c.detail;
    return `- ${label} (${c.resolved ? "resolved" : "open"}): ${c.detail}`;
  });

  return [
    "PREVIOUSLY FLAGGED CONTRADICTIONS — already on the clinician's screen. Report each of " +
      "these again, naming the same topic and subject. Set status to \"resolved\" and fill " +
      "\"resolution\" for any the transcript now settles.",
    ...lines,
  ].join("\n");
}

/**
 * Fingerprint of the known list, for the poll's request-dedup key. Built from ids and
 * resolved state only: those are what change the prompt. Keyed this way so a clinician
 * striking a flag by hand does change the fingerprint (the model must be told), while a
 * re-render does not.
 */
export function contradictionsKey(contradictions) {
  if (!Array.isArray(contradictions) || contradictions.length === 0) return "";
  return djb2(contradictions.map((c) => `${c.id}:${c.resolved ? 1 : 0}`).join("|"));
}

/**
 * Fold a poll's contradictions into the list already on screen.
 *
 * The rules, in the order they matter:
 *
 *   1. A flag absent from the latest response is KEPT, untouched. The model re-reads a
 *      growing transcript every 10 seconds and will drop things it mentioned a moment ago;
 *      a red flag that vanishes on its own is worse than a stale one, because the clinician
 *      has no way to know it was ever there.
 *   2. A clinician's ruling outranks the model's, in BOTH directions. Strike a false positive
 *      and the next poll must not un-strike it; un-strike something you disagree was
 *      clarified and the next poll must not re-strike it. Either way round, a control that
 *      silently undoes itself ten seconds later reads as broken. The model still refreshes
 *      the wording — it just no longer touches the status.
 *   3. Otherwise the model's verdict stands, including reopening what the model itself
 *      resolved: that means the patient contradicted the same subject again, which is exactly
 *      what should go red a second time.
 *   4. Open flags sort above resolved ones, each in the order first seen, so the thing
 *      needing action is at the top and settled history stays put beneath it.
 */
export function mergeContradictions(previous, incoming) {
  const prevList = Array.isArray(previous) ? previous : [];
  const incomingList = Array.isArray(incoming) ? incoming : [];

  const incomingById = new Map(incomingList.map((item) => [item.id, item]));
  const merged = [];

  for (const existing of prevList) {
    const update = incomingById.get(existing.id);
    incomingById.delete(existing.id);

    // Rule 1: nothing came back about this one.
    if (!update) {
      merged.push(existing);
      continue;
    }

    // Rule 2: the clinician has ruled on this one, either way round. `resolved`, `resolution`
    // and `statusSetBy` all carry over untouched from `existing`; only the wording refreshes.
    if (existing.statusSetBy === STATUS_SET_BY.CLINICIAN) {
      merged.push({
        ...existing,
        topic: update.topic || existing.topic,
        subject: update.subject || existing.subject,
        detail: update.detail,
        conflictWith: update.conflictWith ?? existing.conflictWith,
      });
      continue;
    }

    // Rule 3: the model's own verdict, including reopening what it previously resolved.
    merged.push({
      ...existing,
      topic: update.topic || existing.topic,
      subject: update.subject || existing.subject,
      detail: update.detail,
      conflictWith: update.conflictWith ?? existing.conflictWith,
      resolved: update.resolved,
      resolution: update.resolved ? update.resolution || existing.resolution : "",
      statusSetBy: STATUS_SET_BY.MODEL,
    });
  }

  // Whatever is left is new this poll, in the order the model listed it.
  for (const item of incomingList) {
    if (incomingById.has(item.id)) merged.push(item);
  }

  // Rule 4. Stable within each group: sort() is stable in every engine this ships to, so the
  // first-seen order established above survives.
  return merged.sort((a, b) => Number(a.resolved) - Number(b.resolved));
}

/**
 * Flip one contradiction between open and clarified, at the clinician's hand.
 *
 * Both directions are stamped as the clinician's: a manual reopen must not be re-resolved by
 * the model on the next poll any more than a manual resolve should be undone by it. The
 * clinician is in the room and the model is reading a transcript with no speaker labels.
 */
export function toggleContradictionResolved(contradictions, id) {
  // Ids are derived, so they arrive here exactly as deriveId produced them — the component
  // hands back the same string it was given. No normalizing needed on this path.
  const target = asText(id);
  if (!Array.isArray(contradictions) || !target) return contradictions || [];

  const next = contradictions.map((c) => {
    if (c.id !== target) return c;
    return c.resolved
      ? { ...c, resolved: false, resolution: "", statusSetBy: STATUS_SET_BY.CLINICIAN }
      : { ...c, resolved: true, statusSetBy: STATUS_SET_BY.CLINICIAN };
  });

  return next.sort((a, b) => Number(a.resolved) - Number(b.resolved));
}
